import { html, nothing, unsafeCSS, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { when } from "@/common/directives/when";
import styles from "@/index.css?inline";
import EkoElement from "@/utilities/element";
import style from "./notification-menu-item.css?inline";
import "@/components/dot";

@customElement("eko-notification-menu-item")
class NotificationMenuItem extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(styles), unsafeCSS(style)];

  @property()
  author: string = "";

  @property({ reflect: true })
  href?: string;

  @property({ reflect: true, type: Boolean, attribute: "new-since-opening" })
  newSinceOpening: boolean = false;

  @property()
  target?: "_self" | "_blank" | "_parent" | "_top";

  @property()
  timestamp: string = "";

  @property({ type: String, attribute: "data-turbo-frame" })
  turboFrame?: string;

  @property({ type: String, attribute: "data-turbo-method" })
  turboMethod?: string;

  @property({ reflect: true, type: Boolean })
  unread: boolean = false;

  private renderButton(content: TemplateResult): TemplateResult {
    return html`
      <button
        class="eko-notification-menu-item"
        type="button"
        data-turbo-frame=${when(this.turboFrame)}
        data-turbo-method=${when(this.turboMethod)}>
        ${this.newSinceOpening ? html`<div class="eko-notification-menu-item-new"></div>` : nothing}
        ${content}
      </button>
    `;
  }

  private renderContent(): TemplateResult {
    return html`
      <div class="eko-notification-menu-item__inner">
        ${this.unread
          ? html`<div class="eko-notification-menu-item__indicator">
              <eko-dot theme="accent" size="sm"></eko-dot>
            </div>`
          : nothing}
        <div class="eko-notification-menu-item__body">
          <div class="eko-notification-menu-item__header">
            <span class="eko-notification-menu-item__title">
              <slot name="title"></slot>
            </span>
            <span class="eko-notification-menu-item__meta">
              ${this.timestamp}${this.author ? html` · ${this.author}` : nothing}
            </span>
          </div>
          <span class="eko-notification-menu-item__description">
            <slot></slot>
          </span>
        </div>
      </div>
    `;
  }

  private renderLink(content: TemplateResult): TemplateResult {
    return html`
      <a
        class="eko-notification-menu-item"
        href=${when(this.href)}
        rel=${when(this.target === "_blank", "noopener noreferrer")}
        target=${when(this.target)}
        data-turbo-frame=${when(this.turboFrame)}
        data-turbo-method=${when(this.turboMethod)}>
        ${this.newSinceOpening ? html`<div class="eko-notification-menu-item-new"></div>` : nothing}
        ${content}
      </a>
    `;
  }

  render() {
    const content = this.renderContent();
    return this.href ? this.renderLink(content) : this.renderButton(content);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-notification-menu-item": NotificationMenuItem;
  }
}

export default NotificationMenuItem;
